import React, { createContext, useContext } from 'react';
import { useSidePane, type SidePaneState } from '../hooks/useSidePane';

/**
 * THE WORKSTATION'S PANE STATE, held ABOVE the routes.
 *
 * Owner 2026-08-23: *"when I click the wallet icon on far left, the navigation bar is gone — it needs to
 * always be there for navigation convenience."* The activity bar is chrome around the whole signed-in app,
 * not a part of one screen, so it has to be mounted once at the route boundary. But its productions button
 * folds the explorer, and the explorer lives inside `ChatWorkspace` — two `useSidePane` calls would be two
 * independent copies of the same localStorage-backed state, which would drift the moment either changed.
 *
 * So the hook is called ONCE here and both consumers read the same instance. That is the whole job of this
 * context: shared geometry, no data. (The right pane is here too so the pair cannot diverge — same reason
 * they share one hook implementation in the first place.)
 */

interface Workstation {
  left: SidePaneState;
  right: SidePaneState;
}

const WorkstationContext = createContext<Workstation | null>(null);

export const WorkstationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // The left pane keeps its historical localStorage key so nobody's folded explorer resets on deploy.
  const left = useSidePane('left', { collapsed: 'wisetree_sidebar_collapsed', width: 'wisetree_pane_left_width' });
  const right = useSidePane('right', { collapsed: 'wisetree_chat_pane_collapsed', width: 'wisetree_pane_right_width' });
  return <WorkstationContext.Provider value={{ left, right }}>{children}</WorkstationContext.Provider>;
};

/** The shared pane geometry. Throws only if used outside the provider, which is a wiring bug, not a state. */
export function useWorkstation(): Workstation {
  const ctx = useContext(WorkstationContext);
  if (!ctx) throw new Error('useWorkstation must be used inside WorkstationProvider');
  return ctx;
}
